/** Driver-facing dispatch status pipeline.
 *
 *  Stored on `DispatchJob.status` as a free-form string. The admin filter
 *  enum (fleet-ops/configs.ts) still knows about Scheduled / Complete /
 *  Cancelled — we add En route and Arrived as intermediate broadcasts the
 *  driver flips through. Nothing in the schema enforces order; the UI just
 *  hides the buttons that don't apply at each stage. */
export const SCHEDULED = 'Scheduled';
export const EN_ROUTE = 'En route';
export const ARRIVED = 'Arrived';
export const COMPLETE = 'Complete';
export const CANCELLED = 'Cancelled';

/**
 * The pre-unification spelling, still present in stored data.
 *
 * Atlas wrote `'Scheduled Job'` from the dispatch UI while the recurrence
 * spawner (dispatch-job-recurrence.ts) wrote `'Scheduled'` — two writers,
 * two spellings, since long before the partner API existed. The published API
 * enum uses the short form, so that is now the single canonical value and the
 * long one is legacy.
 *
 * This constant is NOT temporary and should not be deleted once the data
 * migration has run. Anything that reintroduces the old value — a Zoho
 * re-import, a restored backup, an un-migrated environment — would otherwise
 * silently render as an unrecognised status. `isScheduled()` is the reason
 * that failure mode cannot come back.
 */
export const SCHEDULED_LEGACY = 'Scheduled Job';

/** True for the canonical value or the legacy one still in stored data. */
export function isScheduled(status: string | null | undefined): boolean {
  return status === SCHEDULED || status === SCHEDULED_LEGACY;
}

/** What's the next action a driver should take from this status? Returns
 *  null when no further action (job is Complete).
 *
 *  Flow: null/Scheduled → En route → Arrived → Complete. */
export function nextStatusAction(status: string | null | undefined):
  | { label: string; next: string; terminal: boolean }
  | null {
  if (status === COMPLETE) return null;
  if (status === ARRIVED)  return { label: 'Mark Complete',  next: COMPLETE,  terminal: true  };
  if (status === EN_ROUTE) return { label: "I've Arrived",   next: ARRIVED,   terminal: false };
  return                          { label: 'Start Drive',    next: EN_ROUTE,  terminal: false };
}

/**
 * Tailwind classes for a solid status bar on the dispatch schedule grid.
 *
 * Lives here, next to the status constants and the driver-facing pill, so the
 * hues stay in step: En route is amber and Arrived is blue on both surfaces.
 * The schedule previously mapped statuses itself and knew only two of them —
 * `'Scheduled Job'` and `'Complete'` — so `En route`, `Arrived`,
 * `Cancelled` and every API-created job all rendered in the same grey as a
 * job with no status at all.
 *
 * Grey is now reserved for genuinely unknown status, which is what it should
 * mean. Cancelled is struck through rather than merely dulled, because "inert"
 * and "unrecognised" need to look different at a glance.
 */
export function scheduleBarClass(status: string | null | undefined): string {
  if (status === COMPLETE) return 'bg-emerald-500 text-white border-emerald-600';
  if (isScheduled(status)) return 'bg-red-500 text-white border-red-600';
  if (status === EN_ROUTE) return 'bg-amber-600 text-white border-amber-700';
  if (status === ARRIVED) return 'bg-blue-600 text-white border-blue-700';
  if (status === CANCELLED) return 'bg-zinc-200 text-zinc-700 border-zinc-400 line-through';
  return 'bg-zinc-400 text-white border-zinc-500';
}

/**
 * Light-background chip for the printed run sheet. Same vocabulary as
 * `scheduleBarClass`, ink-friendly weights. Returns null-ish for an absent
 * status so the caller can render nothing.
 */
export function statusChipClass(status: string | null | undefined): string {
  if (status === COMPLETE) return 'bg-emerald-100 text-emerald-800';
  if (isScheduled(status)) return 'bg-red-100 text-red-800';
  if (status === EN_ROUTE) return 'bg-amber-100 text-amber-800';
  if (status === ARRIVED) return 'bg-blue-100 text-blue-800';
  if (status === CANCELLED) return 'bg-zinc-100 text-zinc-600 line-through';
  return 'bg-zinc-100 text-zinc-700';
}

/** Tailwind classes for the driver-page status pill. The Badge component's
 *  `success` variant already covers Complete; this helper colours the new
 *  intermediate states so a glance across the list shows where each job is. */
export function statusBadgeClass(status: string | null | undefined): string {
  if (status === ARRIVED)  return 'bg-blue-100 text-blue-800 border-transparent dark:bg-blue-900/40 dark:text-blue-200';
  if (status === EN_ROUTE) return 'bg-amber-100 text-amber-800 border-transparent dark:bg-amber-900/40 dark:text-amber-200';
  return '';
}
